import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { coursesAPI, learningAPI } from '../../services/api';
import { getCourseThumbnail } from '../../utils/courseMedia';

const Courses = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [enrolledCourseIds, setEnrolledCourseIds] = useState(new Set());
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [enrollingId, setEnrollingId] = useState(null);

    const pageType = location.pathname === '/courses/my'
        ? 'my'
        : location.pathname === '/courses/free'
            ? 'free'
            : location.pathname === '/courses/paid'
                ? 'paid'
                : 'all';

    const pageTitle = pageType === 'my'
        ? 'My Courses'
        : pageType === 'free'
            ? 'Free Courses'
            : pageType === 'paid'
                ? 'Paid Courses'
                : 'Courses';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [coursesResponse, enrollmentsResponse] = await Promise.all([
                    coursesAPI.getAll(),
                    learningAPI.getEnrollments().catch(() => ({ data: { enrollments: [] } })),
                ]);

                setCourses(coursesResponse.data || []);
                const enrolledIds = new Set((enrollmentsResponse.data?.enrollments || []).map((e) => e.course_id));
                setEnrolledCourseIds(enrolledIds);
            } catch (error) {
                console.error('Failed to load courses', error);
                setCourses([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const preparedCourses = useMemo(() => {
        const base = courses.filter((course) => {
            if (pageType === 'my') return enrolledCourseIds.has(course.id);
            if (pageType === 'free') return Number(course.price || 0) === 0;
            if (pageType === 'paid') return Number(course.price || 0) > 0;
            return true;
        });

        const withProgress = base.map((course) => ({
            ...course,
            progress: enrolledCourseIds.has(course.id) ? 30 : 0,
        }));

        const statusFiltered = withProgress.filter((course) => {
            if (statusFilter === 'in-progress') return course.progress > 0 && course.progress < 100;
            if (statusFilter === 'completed') return course.progress === 100;
            if (statusFilter === 'not-started') return course.progress === 0;
            return true;
        });

        const searched = statusFiltered.filter((course) => {
            const text = `${course.title || ''} ${course.description || ''}`.toLowerCase();
            return text.includes(searchTerm.trim().toLowerCase());
        });

        const sorted = [...searched].sort((a, b) => {
            if (sortBy === 'price-low') return Number(a.price || 0) - Number(b.price || 0);
            if (sortBy === 'price-high') return Number(b.price || 0) - Number(a.price || 0);
            if (sortBy === 'rating') return Number(b.rating || 0) - Number(a.rating || 0);
            return Number(b.id || 0) - Number(a.id || 0);
        });

        return sorted;
    }, [courses, enrolledCourseIds, pageType, searchTerm, sortBy, statusFilter]);

    const handleEnroll = async (e, courseId) => {
        e.stopPropagation();
        if (enrolledCourseIds.has(courseId)) {
            navigate(`/courses/${courseId}`);
            return;
        }

        try {
            setEnrollingId(courseId);
            await learningAPI.enrollInCourse(courseId);
            setEnrolledCourseIds((prev) => new Set([...prev, courseId]));
        } catch (error) {
            console.error('Enrollment failed:', error);
        } finally {
            setEnrollingId(null);
        }
    };

    return (
        <div style={styles.page}>
            <div style={styles.topBar}>
                <h1 style={styles.title}>{pageTitle}</h1>

                <div style={styles.topControls}>
                    <select style={styles.select} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                        <option value="newest">Sort By</option>
                        <option value="rating">Top Rated</option>
                        <option value="price-low">Price: Low to High</option>
                        <option value="price-high">Price: High to Low</option>
                    </select>

                    <select style={styles.select}>
                        <option>Explore</option>
                    </select>

                    <div style={styles.searchWrap}>
                        <Search size={16} color="#9ca3af" />
                        <input
                            type="text"
                            placeholder="Search for anything..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                </div>
            </div>

            <div style={styles.contentWrap}>
                <h2 style={styles.filterHeading}>Filter Courses by</h2>
                <div style={styles.tabs}>
                    <button type="button" style={statusFilter === 'all' ? { ...styles.tab, ...styles.tabActive } : styles.tab} onClick={() => setStatusFilter('all')}>All Courses</button>
                    <button type="button" style={statusFilter === 'in-progress' ? { ...styles.tab, ...styles.tabActive } : styles.tab} onClick={() => setStatusFilter('in-progress')}>In Progress</button>
                    <button type="button" style={statusFilter === 'completed' ? { ...styles.tab, ...styles.tabActive } : styles.tab} onClick={() => setStatusFilter('completed')}>Completed</button>
                    <button type="button" style={statusFilter === 'not-started' ? { ...styles.tab, ...styles.tabActive } : styles.tab} onClick={() => setStatusFilter('not-started')}>Not Started</button>
                </div>

                {loading ? (
                    <div style={styles.loading}>Loading courses...</div>
                ) : preparedCourses.length === 0 ? (
                    <div style={styles.emptyBox}>No courses available yet</div>
                ) : (
                    <div style={styles.grid}>
                        {preparedCourses.map((course) => (
                            <div key={course.id} style={styles.card} onClick={() => navigate(`/courses/${course.id}`)}>
                                {getCourseThumbnail(course) && (
                                    <div style={styles.thumbWrap}>
                                        <img src={getCourseThumbnail(course)} alt={course.title} style={styles.thumb} />
                                    </div>
                                )}

                                <div style={styles.cardBody}>
                                    <h3 style={styles.cardTitle}>{course.title}</h3>
                                    <p style={styles.cardDesc}>{course.description || 'No description available'}</p>
                                    <div style={styles.metaRow}>
                                        <span>⭐ {Number(course.rating || 4.5).toFixed(1)}</span>
                                        <span>{Number(course.price || 0) > 0 ? `₹${course.price}` : 'Free'}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => handleEnroll(e, course.id)}
                                        style={styles.actionBtn}
                                        disabled={enrollingId === course.id}
                                    >
                                        {enrollingId === course.id ? 'Please wait...' : enrolledCourseIds.has(course.id) ? 'Open Course' : 'Enroll'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    page: {
        minHeight: '100vh',
        background: '#f9fafb',
        paddingTop: '82px',
    },
    topBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
        background: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: '14px 28px',
        position: 'sticky',
        top: '72px',
        zIndex: 5,
        flexWrap: 'wrap',
    },
    title: {
        margin: 0,
        fontSize: '40px',
        color: '#111827',
    },
    topControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap',
    },
    select: {
        border: '1px solid #e5e7eb',
        borderRadius: '4px',
        padding: '8px 10px',
        background: '#fff',
        color: '#6b7280',
        fontSize: '12px',
    },
    searchWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        border: '1px solid #e5e7eb',
        borderRadius: '4px',
        background: '#fff',
        padding: '8px 10px',
        minWidth: '240px',
    },
    searchInput: {
        border: 'none',
        outline: 'none',
        width: '100%',
        fontSize: '12px',
    },
    contentWrap: {
        padding: '18px 28px 36px',
    },
    filterHeading: {
        margin: '0 0 12px',
        fontSize: '36px',
        fontWeight: '600',
        color: '#374151',
    },
    tabs: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
        marginBottom: '22px',
    },
    tab: {
        border: '1px solid #e5e7eb',
        background: '#fff',
        color: '#374151',
        borderRadius: '6px',
        padding: '8px 12px',
        fontSize: '12px',
        cursor: 'pointer',
    },
    tabActive: {
        borderColor: '#86efac',
        background: '#dcfce7',
        color: '#166534',
    },
    loading: {
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '30px',
        textAlign: 'center',
        color: '#6b7280',
    },
    emptyBox: {
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        color: '#4b5563',
        fontSize: '20px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '16px',
    },
    card: {
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
        cursor: 'pointer',
    },
    thumbWrap: {
        width: '100%',
        aspectRatio: '16 / 9',
        background: '#e5e7eb',
    },
    thumb: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    cardBody: {
        padding: '12px',
    },
    cardTitle: {
        margin: '0 0 6px',
        color: '#111827',
        fontSize: '18px',
    },
    cardDesc: {
        margin: '0 0 10px',
        color: '#6b7280',
        fontSize: '13px',
        lineHeight: 1.5,
    },
    metaRow: {
        display: 'flex',
        justifyContent: 'space-between',
        color: '#6b7280',
        fontSize: '13px',
        marginBottom: '10px',
    },
    actionBtn: {
        width: '100%',
        border: 'none',
        borderRadius: '8px',
        padding: '10px',
        background: '#4f46e5',
        color: '#fff',
        fontWeight: '600',
        cursor: 'pointer',
    },
};

export default Courses;
